import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, Clock, User, Phone, Mail, Building2, Users, Calendar } from 'lucide-react';
import { api } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const PendingApprovalPage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejectionReason, setRejectionReason] = useState({});
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [expandedInstitutions, setExpandedInstitutions] = useState(new Set());

  // Fetch pending users grouped by institution
  const { data, isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['pending-approval-users'],
    queryFn: async () => {
      try {
        const response = await api.get('/users/pending-approval');
        console.log('Pending approval response:', response.data);
        return response.data.data;
      } catch (error) {
        console.error('Error fetching pending users:', error);
        throw error;
      }
    },
  });

  // Approve user mutation
  const approveMutation = useMutation({
    mutationFn: async (userId) => {
      const response = await api.post(`/users/${userId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Başarılı',
        description: 'Kullanıcı onaylandı',
      });
      queryClient.invalidateQueries(['pending-approval-users']);
    },
    onError: (error) => {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kullanıcı onaylanırken hata oluştu',
        variant: 'destructive',
      });
    },
  });

  // Reject user mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ userId, reason }) => {
      const response = await api.post(`/users/${userId}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Başarılı',
        description: 'Kullanıcı reddedildi',
      });
      setRejectionReason({});
      queryClient.invalidateQueries(['pending-approval-users']);
    },
    onError: (error) => {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Kullanıcı reddedilirken hata oluştu',
        variant: 'destructive',
      });
    },
  });

  // Bulk approve mutation for institution
  const bulkApproveMutation = useMutation({
    mutationFn: async (userIds) => {
      const response = await api.post('/users/bulk-approve', { user_ids: userIds });
      return response.data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Başarılı',
        description: data.message || `${data.data?.approved_count || 0} kullanıcı onaylandı`,
      });
      setSelectedUsers(new Set());
      queryClient.invalidateQueries(['pending-approval-users']);
    },
    onError: (error) => {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Toplu onay işlemi sırasında hata oluştu',
        variant: 'destructive',
      });
    },
  });

  const handleApprove = (userId) => {
    if (confirm('Bu kullanıcıyı onaylamak istediğinize emin misiniz?')) {
      approveMutation.mutate(userId);
    }
  };

  const handleReject = (userId) => {
    const reason = rejectionReason[userId] || '';
    if (!reason.trim()) {
      toast({
        title: 'Hata',
        description: 'Lütfen red nedeni girin',
        variant: 'destructive',
      });
      return;
    }

    if (confirm('Bu kullanıcıyı reddetmek istediğinize emin misiniz?')) {
      rejectMutation.mutate({ userId, reason });
    }
  };

  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAllInInstitution = (institution) => {
    const institutionUserIds = institution.users.map(u => u.id);
    const allSelected = institutionUserIds.every(id => selectedUsers.has(id));
    
    const newSelected = new Set(selectedUsers);
    if (allSelected) {
      institutionUserIds.forEach(id => newSelected.delete(id));
    } else {
      institutionUserIds.forEach(id => newSelected.add(id));
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkApproveInstitution = (institution) => {
    const institutionUserIds = institution.users.map(u => u.id);
    const selectedInInstitution = institutionUserIds.filter(id => selectedUsers.has(id));
    
    if (selectedInInstitution.length === 0) {
      toast({
        title: 'Uyarı',
        description: 'Lütfen en az bir kullanıcı seçin',
        variant: 'destructive',
      });
      return;
    }

    if (confirm(`${selectedInInstitution.length} kullanıcıyı toplu olarak onaylamak istediğinize emin misiniz?`)) {
      bulkApproveMutation.mutate(selectedInInstitution);
    }
  };

  const toggleInstitution = (institutionId) => {
    const newExpanded = new Set(expandedInstitutions);
    if (newExpanded.has(institutionId)) {
      newExpanded.delete(institutionId);
    } else {
      newExpanded.add(institutionId);
    }
    setExpandedInstitutions(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const institutions = data?.institutions || [];
  const totalUsers = data?.total_users || 0;
  const totalInstitutions = data?.total_institutions || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (queryError) {
    console.error('Query error:', queryError);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-6xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {queryError.response?.data?.message || 'Onay bekleyen kullanıcılar yüklenirken hata oluştu'}
                </p>
                <Button onClick={() => refetch()} className="mt-4">
                  Tekrar Dene
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
            Onay Bekleyen Kullanıcılar
          </h1>
          <p className="text-slate-600 mt-2">
            Dış kurumlar tarafından eklenen mobil kullanıcıları onaylayın veya reddedin
          </p>
        </div>

        {/* Summary Card */}
        {totalUsers > 0 && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Clock className="h-6 w-6 text-orange-600" />
                  <div>
                    <p className="font-semibold text-orange-900 text-lg">
                      {totalUsers} kullanıcı onay bekliyor
                    </p>
                    <p className="text-sm text-orange-700">
                      {totalInstitutions} kurumdan gelen istekler
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Institutions List */}
        {institutions.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Onay bekleyen kullanıcı bulunmuyor</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {institutions.map((institution) => {
              const isExpanded = expandedInstitutions.has(institution.institution_id);
              const institutionUserIds = institution.users.map(u => u.id);
              const selectedInInstitution = institutionUserIds.filter(id => selectedUsers.has(id));
              const allSelected = institutionUserIds.length > 0 && institutionUserIds.every(id => selectedUsers.has(id));

              return (
                <Card key={institution.institution_id} className="border-l-4 border-l-blue-500 shadow-lg">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <Building2 className="h-6 w-6 text-blue-600" />
                        <div className="flex-1">
                          <CardTitle className="text-xl">{institution.institution_name}</CardTitle>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Users className="h-4 w-4" />
                              <span>{institution.user_count} kullanıcı eklendi</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-4 w-4" />
                              <span>{formatDate(institution.latest_created_at)}</span>
                            </div>
                            {institution.creator_info && (
                              <div className="flex items-center space-x-1">
                                <User className="h-4 w-4" />
                                <span>
                                  {institution.creator_info.first_name} {institution.creator_info.last_name}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {selectedInInstitution.length > 0 && (
                          <Button
                            onClick={() => handleBulkApproveInstitution(institution)}
                            disabled={bulkApproveMutation.isPending}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {selectedInInstitution.length} Kullanıcıyı Onayla
                          </Button>
                        )}
                        <Button
                          onClick={() => toggleInstitution(institution.institution_id)}
                          variant="outline"
                          size="sm"
                        >
                          {isExpanded ? 'Gizle' : 'Göster'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent>
                      <div className="space-y-4">
                        {/* Select All for Institution */}
                        <div className="flex items-center space-x-3 pb-3 border-b">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => handleSelectAllInInstitution(institution)}
                            className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <Label className="text-sm font-medium">
                            Tümünü Seç ({selectedInInstitution.length} seçili)
                          </Label>
                        </div>

                        {/* Users List */}
                        <div className="space-y-3">
                          {institution.users.map((user) => (
                            <Card key={user.id} className="border-l-2 border-l-orange-400 bg-gray-50">
                              <CardContent className="pt-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start space-x-4 flex-1">
                                    {/* Checkbox */}
                                    <input
                                      type="checkbox"
                                      checked={selectedUsers.has(user.id)}
                                      onChange={() => handleSelectUser(user.id)}
                                      className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mt-1"
                                    />
                                    
                                    <div className="flex-1 space-y-3">
                                      {/* User Info */}
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center space-x-3">
                                          <User className="h-5 w-5 text-gray-400" />
                                          <div>
                                            <p className="text-sm text-gray-600">Ad Soyad</p>
                                            <p className="font-semibold">{user.first_name} {user.last_name}</p>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                          <Mail className="h-5 w-5 text-gray-400" />
                                          <div>
                                            <p className="text-sm text-gray-600">Email</p>
                                            <p className="font-semibold text-sm">{user.email}</p>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                          <Phone className="h-5 w-5 text-gray-400" />
                                          <div>
                                            <p className="text-sm text-gray-600">Telefon</p>
                                            <p className="font-semibold">{user.phone || '-'}</p>
                                          </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                          <Calendar className="h-5 w-5 text-gray-400" />
                                          <div>
                                            <p className="text-sm text-gray-600">Eklenme Tarihi</p>
                                            <p className="font-semibold text-sm">{formatDate(user.created_at)}</p>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Rejection Reason Input */}
                                      <div className="pt-2 border-t">
                                        <Label htmlFor={`reason-${user.id}`} className="text-sm">
                                          Red Nedeni (Reddetmek için doldurun)
                                        </Label>
                                        <Textarea
                                          id={`reason-${user.id}`}
                                          value={rejectionReason[user.id] || ''}
                                          onChange={(e) => setRejectionReason(prev => ({
                                            ...prev,
                                            [user.id]: e.target.value
                                          }))}
                                          placeholder="Kullanıcıyı reddetme nedeni..."
                                          rows={2}
                                          className="mt-1"
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex flex-col space-y-2 ml-4">
                                    <Button
                                      onClick={() => handleApprove(user.id)}
                                      disabled={approveMutation.isPending}
                                      className="bg-green-600 hover:bg-green-700"
                                      size="sm"
                                    >
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Onayla
                                    </Button>
                                    <Button
                                      onClick={() => handleReject(user.id)}
                                      disabled={rejectMutation.isPending}
                                      variant="destructive"
                                      size="sm"
                                    >
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Reddet
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovalPage;
